import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { Button } from '../components/shared/Button';
import { BookOpen, ChevronDown, AlertCircle } from 'lucide-react';
import { examService } from '../services/exam.service';

type Section = 'lezen' | 'luisteren' | 'schrijven' | 'spreken' | 'knm';

interface FormData {
  section: Section;
  showExplanations: boolean;
}

interface FormErrors {
  section?: string;
  api?: string;
}

const sectionToExamType: Record<Section, string> = {
  lezen: 'reading',
  luisteren: 'listening',
  schrijven: 'writing',
  spreken: 'speaking',
  knm: 'knm',
};

const sectionToRoute: Record<Section, string> = {
  lezen: '/exam/reading',
  luisteren: '/exam/listening',
  schrijven: '/exam/writing',
  spreken: '/exam/speaking',
  knm: '/exam/knm',
};

export const TryFreePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    section: 'lezen',
    showExplanations: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.section) {
      newErrors.section = t('validation.sectionRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const examType = sectionToExamType[formData.section];
      const exams = await examService.getExams({ type: examType });

      // Prefer a free (trial) exam; KNM has one, as do the four language sections.
      const freeExam = exams.find((ex) => !ex.requiresSubscription);
      const exam = freeExam || exams[0];

      if (!exam) {
        setErrors({ api: t('auth.tryFree.noExamAvailable') });
        setIsSubmitting(false);
        return;
      }

      const attempt = await examService.startExam(exam._id);
      const route = sectionToRoute[formData.section];
      navigate(`${route}?attemptId=${attempt.attemptId}`);
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Er ging iets mis. Probeer het opnieuw.';
      setErrors({ api: msg });
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <AuthLayout
      variant="try-free"
      title={t('auth.tryFree.title')}
      subtitle={t('auth.tryFree.subtitle')}
    >
      <AuthCard
        label={t('auth.tryFree.cardTitle')}
        labelColor="secondary"
        title={t('auth.tryFree.cardTitle')}
        subtitle={t('auth.tryFree.cardSubtitle')}
        footer={{
          text: t('auth.tryFree.hasAccount'),
          linkText: t('header.login'),
          href: '/login',
        }}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {errors.api && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.api}
            </div>
          )}

          {/* Section Select */}
          <div>
            <label 
              htmlFor="section" 
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('auth.tryFree.section')}
            </label>
            <div className="relative">
              <select
                id="section"
                value={formData.section}
                onChange={(e) => handleChange('section', e.target.value as Section)}
                className={`w-full appearance-none border rounded-lg px-4 py-3 pr-10 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                  errors.section ? 'border-error' : 'border-gray-300'
                }`}
                aria-invalid={!!errors.section}
                aria-describedby={errors.section ? 'section-error' : undefined}
              >
                <option value="lezen">{t('auth.tryFree.sectionOptions.reading')}</option>
                <option value="luisteren">{t('auth.tryFree.sectionOptions.listening')}</option>
                <option value="schrijven">{t('auth.tryFree.sectionOptions.writing')}</option>
                <option value="spreken">{t('auth.tryFree.sectionOptions.speaking')}</option>
                <option value="knm">{t('auth.tryFree.sectionOptions.knm')}</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
            {errors.section && (
              <p id="section-error" className="text-sm text-error mt-1" role="alert">
                {errors.section}
              </p>
            )}
          </div>

          {/* Show Explanations Checkbox */}
          <div className="flex items-start gap-3">
            <div className="flex items-center h-6">
              <input
                id="showExplanations"
                type="checkbox"
                checked={formData.showExplanations}
                onChange={(e) => handleChange('showExplanations', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
              />
            </div>
            <label 
              htmlFor="showExplanations" 
              className="text-sm text-gray-700 cursor-pointer"
            >
              <span className="font-medium">{t('auth.tryFree.showExplanations')}</span>
              <span className="block text-gray-500 mt-0.5">
                {t('auth.tryFree.showExplanationsHint')}
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            size="large"
            className="w-full bg-secondary hover:bg-orange-500"
            loading={isSubmitting}
            disabled={isSubmitting}
            icon={<BookOpen className="w-5 h-5" />}
          >
            {isSubmitting ? t('common.waiting') : t('auth.tryFree.cta')}
          </Button>

          <p className="text-xs text-center text-gray-500">
            {t('auth.tryFree.afterExamNote')}
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default TryFreePage;
